import { useMemo } from "react";
import { cn } from "@/lib/cn.js";
import { colorForKey, colorForKeyBorder, isoDate } from "@/lib/format.js";
import type { Appointment, Customer, Service, TenantClosure } from "@/lib/types.js";
import { pickDayClosure, tenantDayLabel } from "./closures-overlay.js";

/**
 * Month view (PRD calendar-ux-parity REQ-CUX-004 / REQ-CUX-013): the standard
 * 6×7 = 42-cell calendar grid for the visible month. Each cell shows the day
 * number, an appointment-count badge, and up to 3 appointment chips. Cells
 * outside the focal month render dimmed but are still interactive (clicking
 * navigates to that date's day view, REQ-CUX-013).
 *
 * Closed-day overlay (REQ-CUX-012) is per-cell — closed days get a striped
 * background and a closure-reason chip in the upper-left corner, matching
 * the day-view treatment.
 *
 * Performance (NFR-CUX-002): the appointment-by-day map is memoized off the
 * appointments + tenantTz pair so re-renders during interaction don't redo
 * the bucketing. With ≤50 appointments/day × 42 cells (~2.1k items max),
 * this stays well inside the 16ms paint budget on a Chromebook-class device.
 */
const MAX_VISIBLE_CHIPS = 3;

export function CalendarMonthGrid({
  day,
  tenantTz,
  appointments,
  services,
  customers,
  closures,
  onClickDay
}: {
  day: Date;
  tenantTz: string;
  appointments: Appointment[];
  services: Service[];
  customers: Customer[];
  closures: TenantClosure[];
  /** Click handler for any cell — fires with the cell's YYYY-MM-DD (tenant-local). */
  onClickDay: (dateIso: string) => void;
}): React.JSX.Element {
  const cells = useMemo(() => buildCells(day), [day]);
  const focalMonth = day.getMonth();
  const today = isoDate(new Date());
  const serviceById = useMemo(() => new Map(services.map((s) => [s.id, s])), [services]);
  const customerById = useMemo(() => new Map(customers.map((c) => [c.id, c])), [customers]);

  // Bucket appointments by tenant-local YYYY-MM-DD for O(1) per-cell lookup.
  const byDay = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const appointment of appointments) {
      const key = tenantDayLabel(appointment.starts_at, tenantTz);
      let bucket = map.get(key);
      if (!bucket) {
        bucket = [];
        map.set(key, bucket);
      }
      bucket.push(appointment);
    }
    // Sort each bucket by starts_at for stable rendering.
    for (const bucket of map.values()) {
      bucket.sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at));
    }
    return map;
  }, [appointments, tenantTz]);

  return (
    <div data-testid="calendar-month-grid" className="overflow-hidden rounded-lg border bg-card shadow-xs">
      {/* Mobile agenda: 7×6 grid is unreadable below ~480px (cells ≈ 50px wide).
          Render a vertical list of days that have appointments or closures
          instead, ordered chronologically. The grid view stays available on
          tablet+. */}
      <div className="lg:hidden">
        <MonthAgenda
          cells={cells}
          tenantTz={tenantTz}
          today={today}
          byDay={byDay}
          serviceById={serviceById}
          customerById={customerById}
          closures={closures}
          onClickDay={onClickDay}
        />
      </div>

      {/* Weekday header */}
      <div className="hidden grid-cols-7 border-b bg-muted/40 lg:grid">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </div>
        ))}
      </div>
      {/* 6×7 cells (desktop) */}
      <div className="hidden grid-cols-7 lg:grid" style={{ gridAutoRows: "minmax(96px, 1fr)" }}>
        {cells.map((cellDate) => {
          const iso = isoDate(cellDate);
          const inFocalMonth = cellDate.getMonth() === focalMonth;
          const isToday = iso === today;
          const dayLabel = tenantDayLabel(cellDate, tenantTz);
          const dayClosure = pickDayClosure(closures, dayLabel, tenantTz);
          const dayAppointments = byDay.get(dayLabel) ?? [];
          const overflow = dayAppointments.length - MAX_VISIBLE_CHIPS;
          return (
            <button
              type="button"
              key={iso}
              data-testid={`month-day-${iso}`}
              data-closed={dayClosure ? "true" : "false"}
              onClick={() => onClickDay(iso)}
              className={cn(
                "relative flex flex-col items-stretch gap-1 border-b border-l p-1.5 text-left transition-colors duration-100 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none",
                !inFocalMonth && "bg-muted/30 text-muted-foreground/70",
                isToday && "ring-1 ring-primary/40"
              )}
              style={
                dayClosure
                  ? {
                      backgroundImage:
                        "repeating-linear-gradient(45deg, rgba(160, 160, 160, 0.18) 0, rgba(160, 160, 160, 0.18) 6px, transparent 6px, transparent 12px)"
                    }
                  : undefined
              }
            >
              <div className="flex items-center justify-between text-[11px]">
                <span className={cn("tabular-nums font-semibold", isToday && "text-primary")}>
                  {cellDate.getDate()}
                </span>
                {dayAppointments.length > 0 && (
                  <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold tabular-nums text-primary">
                    {dayAppointments.length}
                  </span>
                )}
              </div>
              {dayClosure && (
                <span
                  data-testid={`month-day-closure-chip-${iso}`}
                  className="inline-flex max-w-full items-center gap-1 truncate rounded-md bg-warning/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning-foreground"
                >
                  Closed · {dayClosure.reason}
                </span>
              )}
              {dayAppointments.slice(0, MAX_VISIBLE_CHIPS).map((appointment) => {
                const service = serviceById.get(appointment.service_id);
                const customer = customerById.get(appointment.customer_id);
                const colorKey = service?.id ?? "default";
                return (
                  <span
                    key={appointment.id}
                    data-testid={`month-chip-${appointment.id}`}
                    className="truncate rounded-sm border-l-[3px] px-1 text-[10px] leading-snug"
                    style={{
                      background: colorForKey(colorKey),
                      borderLeftColor: colorForKeyBorder(colorKey)
                    }}
                  >
                    <span className="font-semibold">{service?.name ?? "Appt"}</span>
                    {" · "}
                    <span className="text-muted-foreground">{appointment.customer_name ?? customer?.name ?? "—"}</span>
                  </span>
                );
              })}
              {overflow > 0 && (
                <span className="text-[10px] text-muted-foreground">+{overflow} more</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MonthAgenda({
  cells,
  tenantTz,
  today,
  byDay,
  serviceById,
  customerById,
  closures,
  onClickDay
}: {
  cells: Date[];
  tenantTz: string;
  today: string;
  byDay: Map<string, Appointment[]>;
  serviceById: Map<string, Service>;
  customerById: Map<string, Customer>;
  closures: TenantClosure[];
  onClickDay: (dateIso: string) => void;
}): React.JSX.Element {
  // Walk the 42-cell grid in order, but only render days with appointments
  // OR a closure overlay. Empty days are summarized by a count line.
  type AgendaItem = {
    date: Date;
    iso: string;
    appointments: Appointment[];
    closure: ReturnType<typeof pickDayClosure>;
  };
  const items: AgendaItem[] = cells.map((date) => {
    const iso = isoDate(date);
    const dayLabel = tenantDayLabel(date, tenantTz);
    return {
      date,
      iso,
      appointments: byDay.get(dayLabel) ?? [],
      closure: pickDayClosure(closures, dayLabel, tenantTz)
    };
  });
  const interesting = items.filter((it) => it.appointments.length > 0 || it.closure);

  if (interesting.length === 0) {
    return (
      <div className="px-4 py-12 text-center text-sm text-muted-foreground">
        Nothing scheduled this month.
      </div>
    );
  }

  return (
    <ul className="flex flex-col divide-y">
      {interesting.map((item) => (
        <li key={item.iso}>
          <button
            type="button"
            onClick={() => onClickDay(item.iso)}
            data-testid={`month-agenda-${item.iso}`}
            data-closed={item.closure ? "true" : "false"}
            className={cn(
              "flex w-full items-stretch gap-3 px-4 py-3 text-left transition-colors duration-100 hover:bg-muted/40 focus-visible:outline-none focus-visible:bg-muted/60",
              item.iso === today && "bg-primary/5"
            )}
          >
            <div className="flex w-12 flex-col items-center justify-center text-center">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {item.date.toLocaleDateString(undefined, { weekday: "short" })}
              </span>
              <span
                className={cn(
                  "text-lg font-semibold tabular-nums",
                  item.iso === today && "text-primary"
                )}
              >
                {item.date.getDate()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              {item.closure && (
                <span
                  data-testid={`month-agenda-closure-chip-${item.iso}`}
                  className="mb-1 inline-flex items-center gap-1 rounded-md bg-warning/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning-foreground"
                >
                  Closed · {item.closure.reason}
                </span>
              )}
              {item.appointments.length === 0 ? (
                <div className="text-xs text-muted-foreground">No appointments</div>
              ) : (
                <ul className="flex flex-col gap-0.5">
                  {item.appointments.slice(0, 3).map((appointment) => {
                    const service = serviceById.get(appointment.service_id);
                    const customer = customerById.get(appointment.customer_id);
                    return (
                      <li
                        key={appointment.id}
                        data-testid={`month-agenda-chip-${appointment.id}`}
                        className="truncate text-xs"
                      >
                        <span className="font-medium text-foreground">
                          {new Date(appointment.starts_at).toLocaleTimeString(undefined, {
                            hour: "numeric",
                            minute: "2-digit"
                          })}
                        </span>{" "}
                        <span>{service?.name ?? "Appt"}</span>
                        {" · "}
                        <span className="text-muted-foreground">{appointment.customer_name ?? customer?.name ?? "—"}</span>
                      </li>
                    );
                  })}
                  {item.appointments.length > 3 && (
                    <li className="text-[10px] text-muted-foreground">
                      +{item.appointments.length - 3} more
                    </li>
                  )}
                </ul>
              )}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

/**
 * Build the 42 dates that compose the visible month grid. Anchors on the
 * Monday of the week containing the first of the month, then walks forward
 * 42 days. This always produces 6 rows × 7 cols regardless of where the
 * month starts; partial cells from the prior/next month render dimmed.
 */
function buildCells(day: Date): Date[] {
  const first = new Date(day.getFullYear(), day.getMonth(), 1);
  // Mon=0..Sun=6 anchor.
  const anchorOffset = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - anchorOffset);
  start.setHours(0, 0, 0, 0);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}
